<script setup lang="ts">
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { fortnoxAuthService } from '@/services/auth/fortnox-auth-service';
import { supplierInvoiceService } from '@/services/invoices/supplier-invoice-service';
import {
  PhCaretDoubleRight,
  PhCheckCircle,
  PhLetterCircleP,
  PhUserCircleDashed,
  PhXCircle,
} from '@phosphor-icons/vue';
import { models, utils } from 'cesg-shared';
import { onMounted, type PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const route = useRoute();

const props = defineProps({
  invoiceReviewers: {
    type: Array as PropType<models.FortnoxUserReviewStatus[]>,
    required: true,
  },
  hide: {
    type: Boolean,
    required: false,
    default: false,
  },
  beforeReview: {
    type: Function as PropType<() => Promise<boolean>>,
    required: false,
    default: undefined,
  },
});

const currentUser = ref<models.GetMeResponse | null>(null);

const reviewerClass = (invoice: models.FortnoxUserReviewStatus) => {
  return {
    approved:
      invoice.reviewStatus === models.FortnoxUserReviewStatusType.APPROVE,
    contested:
      invoice.reviewStatus === models.FortnoxUserReviewStatusType.CONTEST,
    parked: invoice.reviewStatus === models.FortnoxUserReviewStatusType.PARK,
    pending:
      invoice.reviewStatus === models.FortnoxUserReviewStatusType.PENDING,
  };
};

function invoiceLocked(index: number) {
  return props.invoiceReviewers
    .slice(0, index)
    .some(
      (reviewer) =>
        reviewer.reviewStatus === models.FortnoxUserReviewStatusType.CONTEST ||
        reviewer.reviewStatus === models.FortnoxUserReviewStatusType.PARK,
    );
}

function canReviewInvoice() {
  function reviewStatusIs(reviewStatus: models.FortnoxUserReviewStatusType) {
    return props.invoiceReviewers[userIndex].reviewStatus === reviewStatus;
  }
  function allPreviousApproved(index: number) {
    return props.invoiceReviewers
      .slice(0, index)
      .every(
        (reviewer) =>
          reviewer.reviewStatus === models.FortnoxUserReviewStatusType.APPROVE,
      );
  }

  if (!currentUser.value) return false;

  const userIndex = props.invoiceReviewers.findIndex(
    (reviewer) => reviewer.id === currentUser.value?.id,
  );

  if (userIndex === -1) return false;

  return (
    allPreviousApproved(userIndex) &&
    (reviewStatusIs(models.FortnoxUserReviewStatusType.PENDING) ||
      reviewStatusIs(models.FortnoxUserReviewStatusType.PARK) ||
      reviewStatusIs(models.FortnoxUserReviewStatusType.CONTEST))
  );
}

async function goToInvoiceList() {
  await router.push({
    name: RouteName.Invoices,
    // `id` is the project id inherited from the parent `/projekt/:id` route
    // (not the invoice id — that's `route.params.invoiceId`).
    params: { id: route.params.id },
  });
}

async function approveInvoice() {
  if (props.beforeReview && !(await props.beforeReview())) return;
  await supplierInvoiceService.approveSupplierInvoice(
    utils.type.asType<string>(route.params.invoiceId),
  );
  await goToInvoiceList();
}

async function contestInvoice() {
  if (props.beforeReview && !(await props.beforeReview())) return;
  await supplierInvoiceService.contestSupplierInvoice(
    utils.type.asType<string>(route.params.invoiceId),
  );
  await goToInvoiceList();
}

async function parkInvoice() {
  if (props.beforeReview && !(await props.beforeReview())) return;
  await supplierInvoiceService.parkSupplierInvoice(
    utils.type.asType<string>(route.params.invoiceId),
  );
  await goToInvoiceList();
}

async function fetchCurrentUser() {
  currentUser.value = await fortnoxAuthService.getMe();
}

onMounted(() => {
  fetchCurrentUser();
});
</script>

<template>
  <div class="invoice-reviewers" v-if="invoiceReviewers.length > 0">
    <div class="reviewers">
      <div
        class="reviewer"
        v-for="(reviewer, i) in invoiceReviewers"
        :key="reviewer.id"
      >
        <div class="reviewer-info" :class="{ locked: invoiceLocked(i) }">
          <p v-if="currentUser?.id === reviewer.id">{{ t('you') }}</p>
          <p v-else>{{ reviewer.name }}</p>

          <div class="review-status" :class="reviewerClass(reviewer)">
            <PhUserCircleDashed
              :size="32"
              v-if="reviewer.reviewStatus === 'PENDING'"
            />
            <PhXCircle
              :size="32"
              v-else-if="reviewer.reviewStatus === 'CONTEST'"
            />
            <PhCheckCircle
              :size="32"
              v-else-if="reviewer.reviewStatus === 'APPROVE'"
            />
            <PhLetterCircleP
              :size="32"
              v-else-if="reviewer.reviewStatus === 'PARK'"
            />
          </div>
        </div>
        <PhCaretDoubleRight :size="32" v-if="i < invoiceReviewers.length - 1" />
      </div>
    </div>
  </div>

  <div class="actions" v-if="!hide">
    <button
      class="button is-success"
      :disabled="!canReviewInvoice()"
      @click.prevent.stop="approveInvoice"
      data-test="approve-invoice"
    >
      {{ t('invoices.details.reviewActions.approve') }}
    </button>
    <button
      class="button is-danger"
      :disabled="!canReviewInvoice()"
      @click.prevent.stop="contestInvoice"
      data-test="contest-invoice"
    >
      {{ t('invoices.details.reviewActions.contest') }}
    </button>
    <button
      class="button is-warning"
      :disabled="!canReviewInvoice()"
      @click.prevent.stop="parkInvoice"
      data-test="park-invoice"
    >
      {{ t('invoices.details.reviewActions.park') }}
    </button>
  </div>
</template>

<style scoped lang="scss">
.reviewers {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  .reviewer {
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    font-weight: 500;
    color: var(--text-color);

    .reviewer-info {
      font-size: 1rem;
      font-weight: 400;

      border-radius: 0.25rem;

      padding: 0.5rem 1rem 0.5rem 0;
      display: flex;
      align-items: center;
      gap: 1rem;

      &.locked {
        text-decoration: line-through;
        opacity: 0.5;
      }

      .review-status {
        display: flex;
        align-items: center;

        border-radius: 50%;
        padding: 0.1rem;

        &.approved {
          background-color: var(--table-green);
        }

        &.contested {
          background-color: var(--table-red);
        }

        &.parked {
          background-color: var(--table-orange);
        }

        &.pending {
          background-color: var(--table-blue);
        }
      }
    }
  }
}

.actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}
</style>
